"use client";

import React, { useState, useEffect } from "react";
import { useTranslations } from "next-intl";
import {
  MdOutlineSpaceDashboard,
  MdLayers,
  MdCalendarToday,
  MdPercent,
  MdOutlineHandyman,
  MdEventAvailable,
  MdTag,
  MdErrorOutline,
} from "react-icons/md";
import { TypeEnum, PurposeEnum } from "@/types/types";
import styles from "./property-specs.module.css";
import { useUserAuthDataContext } from "@/global-context/user-auth-context";
import { useListingContext } from "../listingContex";

// Math Helpers for Database Integrity (Always Integer)
export function sqftToM2(sqft: number): number {
  return Math.round(sqft / 10.764);
}
export function m2ToSqft(m2: number): number {
  return Math.round(m2 * 10.764);
}

export default function PropertySpecsSection() {
  const t = useTranslations("NewListing");
  const { data, updateField, validationErrors } = useListingContext();
  const { userData } = useUserAuthDataContext();
  const unit_system = userData.unit_system || "Metric";

  // Local state for the split levels logic
  const [propertyLevel, setPropertyLevel] = useState("");
  const [totalLevels, setTotalLevels] = useState("");

  // Determine if Year Built is in the future
  const currentYear = new Date().getFullYear();
  const isFutureBuild = data?.year_built
    ? data.year_built > currentYear
    : false;

  // Sync local levels state with store on mount/change
  useEffect(() => {
    if (data?.levels) {
      if (data.type === TypeEnum.APARTMENT || data.type === TypeEnum.OFFICE) {
        const parts = data.levels.split("/");
        if (parts.length >= 1) setPropertyLevel(parts[0].trim());
        if (parts.length >= 2) setTotalLevels(parts[1].trim());
      } else {
        setPropertyLevel(data.levels);
      }
    }
  }, [data?.levels, data?.type]);

  if (!data) return <PropertySpecsSkeleton />;

  // Level Update Handler
  const handleLevelChange = (propLevel: string, totLevel: string) => {
    setPropertyLevel(propLevel);
    setTotalLevels(totLevel);

    if (data.type === TypeEnum.HOUSE) {
      updateField("levels", propLevel);
    } else {
      // Only combine if there's actual data to prevent weird " / " saves
      if (propLevel || totLevel) {
        updateField("levels", `${propLevel} / ${totLevel}`);
      } else {
        updateField("levels", "");
      }
    }
  };

  const isLand = data.type === TypeEnum.LAND;
  const isHouseOrApt =
    data.type === TypeEnum.APARTMENT || data.type === TypeEnum.HOUSE;

  return (
    <section className={styles.transparentContainer}>
      <h2 className={styles.sectionTitle}>Property Specifications</h2>

      {/* 1. QUANTITY SELECTORS (Rooms, Beds, Baths) */}
      {isHouseOrApt && (
        <div className={styles.selectorsGrid}>
          <CountSelector
            id="number_of_rooms"
            label="Total Rooms"
            value={data.number_of_rooms}
            onChange={(v) => updateField("number_of_rooms", v)}
            error={validationErrors?.number_of_rooms}
            t={t}
            isMandatory={true}
          />
          <CountSelector
            id="number_of_bedrooms"
            label="Bedrooms"
            value={data.number_of_bedrooms}
            onChange={(v) => updateField("number_of_bedrooms", v)}
            error={validationErrors?.number_of_bedrooms}
            t={t}
            isMandatory={true}
          />
          <CountSelector
            id="number_of_full_bathrooms"
            label="Full Bathrooms"
            value={data.number_of_full_bathrooms}
            onChange={(v) => updateField("number_of_full_bathrooms", v)}
            error={validationErrors?.number_of_full_bathrooms}
            t={t}
            isMandatory={false}
          />
          <CountSelector
            id="number_of_bathrooms"
            label="Bathrooms"
            value={data.number_of_bathrooms}
            onChange={(v) => updateField("number_of_bathrooms", v)}
            error={validationErrors?.number_of_bathrooms}
            t={t}
            isMandatory={true}
          />
        </div>
      )}

      {/* 2. AREA & DIMENSIONS ROW */}
      <div className={styles.inputGrid}>
        {!isLand && (
          <AreaInput
            id="interior_space"
            label="Interior Space"
            valueInM2={data.interior_space || 0}
            onChange={(v) => updateField("interior_space", v)}
            unitSystem={unit_system}
            error={validationErrors?.interior_space}
            t={t}
          />
        )}

        {data.type !== TypeEnum.APARTMENT && (
          <AreaInput
            id="lot_size"
            label="Lot Size"
            valueInM2={data.lot_size || 0}
            onChange={(v) => updateField("lot_size", v)}
            unitSystem={unit_system}
            error={validationErrors?.lot_size}
            t={t}
          />
        )}

        {!isLand && (
          <AreaInput
            id="total_structure_area"
            label="Total Structure Area"
            valueInM2={data.total_structure_area || 0}
            onChange={(v) => updateField("total_structure_area", v)}
            unitSystem={unit_system}
            error={validationErrors?.total_structure_area}
            t={t}
          />
        )}
      </div>

      {/* 3. LEVELS, YEAR BUILT, BUYERS FEE */}
      <div className={styles.inputGrid}>
        {/* DYNAMIC LEVELS INPUT */}
        {!isLand && (
          <div id="levels" className={styles.inputGroup}>
            <div className={styles.labelWithInfo}>
              <label className={styles.inputLabel}>
                {data.type === TypeEnum.HOUSE
                  ? "Number of Floors"
                  : "Floor / Total Floors"}{" "}
                <span className={styles.asterisk}>*</span>
              </label>
              {validationErrors?.levels && (
                <span className={styles.inlineError}>
                  <MdErrorOutline /> {t(`errors.${validationErrors.levels}`)}
                </span>
              )}
            </div>
            <div className={styles.levelsWrapper}>
              <div
                className={`${styles.iconInputWrapper} ${validationErrors?.levels ? styles.inputError : ""}`}
              >
                <MdLayers className={styles.inputIcon} />
                <input
                  type="text"
                  maxLength={3}
                  value={propertyLevel}
                  onChange={(e) =>
                    handleLevelChange(
                      e.target.value.replace(/\D/g, ""),
                      totalLevels,
                    )
                  }
                  placeholder={data.type === TypeEnum.HOUSE ? "Ex: 2" : "Floor"}
                  className={styles.textInput}
                />
              </div>

              {(data.type === TypeEnum.APARTMENT ||
                data.type === TypeEnum.OFFICE) && (
                <>
                  <span className={styles.levelDivider}>/</span>
                  <div
                    className={`${styles.iconInputWrapper} ${validationErrors?.levels ? styles.inputError : ""}`}
                  >
                    <input
                      type="text"
                      maxLength={3}
                      value={totalLevels}
                      onChange={(e) =>
                        handleLevelChange(
                          propertyLevel,
                          e.target.value.replace(/\D/g, ""),
                        )
                      }
                      placeholder="Total"
                      className={styles.textInput}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* YEAR BUILT */}
        {!isLand && (
          <div id="year_built" className={styles.inputGroup}>
            <div className={styles.labelWithInfo}>
              <label className={styles.inputLabel}>
                Year Built <span className={styles.asterisk}>*</span>
              </label>
              {validationErrors?.year_built && (
                <span className={styles.inlineError}>
                  <MdErrorOutline />{" "}
                  {t(`errors.${validationErrors.year_built}`)}
                </span>
              )}
            </div>
            <div
              className={`${styles.iconInputWrapper} ${validationErrors?.year_built ? styles.inputError : ""}`}
            >
              <MdCalendarToday className={styles.inputIcon} />
              <input
                type="number"
                value={data.year_built || ""}
                onChange={(e) => {
                  const newYear = Number(e.target.value);
                  updateField("year_built", newYear);
                  // Clear available_from date if they change year to past/present
                  if (newYear <= currentYear) {
                    updateField("available_from", "");
                  }
                }}
                placeholder={`Ex: ${currentYear}`}
                className={styles.textInput}
              />
            </div>
          </div>
        )}

        {/* 🚀 CONDITIONAL: AVAILABLE FROM DATE (Only if future year) */}
        {!isLand && isFutureBuild && (
          <div
            id="available_from"
            className={styles.inputGroup}
            style={{ animation: "fadeIn 0.3s ease" }}
          >
            <div className={styles.labelWithInfo}>
              <label className={styles.inputLabel}>
                Available From <span className={styles.asterisk}>*</span>
              </label>
              {validationErrors?.available_from && (
                <span className={styles.inlineError}>
                  <MdErrorOutline />{" "}
                  {t(`errors.${validationErrors.available_from}`)}
                </span>
              )}
            </div>
            <div
              className={`${styles.iconInputWrapper} ${validationErrors?.available_from ? styles.inputError : ""}`}
            >
              <MdEventAvailable className={styles.inputIcon} />
              <input
                type="date"
                value={data.available_from || ""}
                onChange={(e) => updateField("available_from", e.target.value)}
                className={styles.textInput}
                style={{ color: data.available_from ? "#1a1c1e" : "#cbd5e1" }} // Fixes native datepicker placeholder color
              />
            </div>
          </div>
        )}

        {/* BUYERS FEE */}
        {data.purpose !== PurposeEnum.FORRENT && (
          <div id="buyers_fee" className={styles.inputGroup}>
            <div className={styles.labelWithInfo}>
              <label className={styles.inputLabel}>Buyer&apos;s Fee (%)</label>
              {validationErrors?.buyers_fee && (
                <span className={styles.inlineError}>
                  <MdErrorOutline />{" "}
                  {t(`errors.${validationErrors.buyers_fee}`)}
                </span>
              )}
            </div>
            <div
              className={`${styles.iconInputWrapper} ${validationErrors?.buyers_fee ? styles.inputError : ""}`}
            >
              <MdPercent className={styles.inputIcon} />
              <input
                type="number"
                min={0}
                max={30}
                value={data.buyers_fee || ""}
                onChange={(e) =>
                  updateField("buyers_fee", Number(e.target.value))
                }
                placeholder="0"
                className={styles.textInput}
              />
            </div>
          </div>
        )}
      </div>

      {/* 4. MATERIALS & REFERENCE ID */}
      <div className={styles.inputGrid}>
        {!isLand && (
          <div id="construction_materials" className={styles.inputGroup}>
            <div className={styles.labelWithInfo}>
              <label className={styles.inputLabel}>
                Construction Materials
              </label>
              {validationErrors?.construction_materials && (
                <span className={styles.inlineError}>
                  <MdErrorOutline />{" "}
                  {t(`errors.${validationErrors.construction_materials}`)}
                </span>
              )}
            </div>
            <div
              className={`${styles.iconInputWrapper} ${validationErrors?.construction_materials ? styles.inputError : ""}`}
            >
              <MdOutlineHandyman className={styles.inputIcon} />
              <input
                type="text"
                value={data.construction_materials || ""}
                onChange={(e) =>
                  updateField("construction_materials", e.target.value)
                }
                placeholder="Ex: Brick, Concrete, Steel..."
                className={styles.textInput}
              />
            </div>
          </div>
        )}

        {/* 🚀 NEW: REFERENCE ID / CADASTRAL NUMBER */}
        <div id="reference_id" className={styles.inputGroup}>
          <div className={styles.labelWithInfo}>
            <label className={styles.inputLabel}>Property Reference ID</label>
            {validationErrors?.reference_id && (
              <span className={styles.inlineError}>
                <MdErrorOutline />{" "}
                {t(`errors.${validationErrors.reference_id}`)}
              </span>
            )}
          </div>
          <div
            className={`${styles.iconInputWrapper} ${validationErrors?.reference_id ? styles.inputError : ""}`}
          >
            <MdTag className={styles.inputIcon} />
            <input
              type="text"
              value={data.reference_id || ""}
              onChange={(e) => updateField("reference_id", e.target.value)}
              placeholder="Ex: Numar Cadastral / MLS ID"
              className={styles.textInput}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

// -------------------------------------------------------------
// SUB-COMPONENT: Safe Area Input (Stops Jumping Cursors!)
// -------------------------------------------------------------
function AreaInput({
  id,
  label,
  valueInM2,
  onChange,
  unitSystem,
  error,
  t,
}: {
  id: string;
  label: string;
  valueInM2: number;
  onChange: (v: number) => void;
  unitSystem: string;
  error?: string;
  t: any;
}) {
  const [localValue, setLocalValue] = useState<string>("");

  useEffect(() => {
    if (valueInM2) {
      setLocalValue(
        unitSystem === "Imperial"
          ? m2ToSqft(valueInM2).toString()
          : valueInM2.toString(),
      );
    } else {
      setLocalValue("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitSystem]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/\D/g, ""); // Remove non-digits
    setLocalValue(rawValue);

    const numericValue = Number(rawValue) || 0;

    // Always store M2 in the backend database
    if (unitSystem === "Imperial") {
      onChange(sqftToM2(numericValue));
    } else {
      onChange(Math.round(numericValue));
    }
  };

  return (
    <div id={id} className={styles.inputGroup}>
      <div className={styles.labelWithInfo}>
        <label className={styles.inputLabel}>
          {label} <span className={styles.asterisk}>*</span>
        </label>
        {error && (
          <span className={styles.inlineError}>
            <MdErrorOutline /> {t(`errors.${error}`)}
          </span>
        )}
      </div>
      <div
        className={`${styles.iconInputWrapper} ${error ? styles.inputError : ""}`}
      >
        <MdOutlineSpaceDashboard className={styles.inputIcon} />
        <input
          type="text"
          value={localValue}
          onChange={handleChange}
          placeholder="0"
          className={styles.textInput}
        />
        <span className={styles.unitPill}>
          {unitSystem === "Imperial" ? "sqft" : "mp"}
        </span>
      </div>
    </div>
  );
}

// Sub-component for the Number Selectors
function CountSelector({
  id,
  label,
  value,
  onChange,
  error,
  t,
  isMandatory,
}: {
  id: string;
  label: string;
  value: number;
  onChange: (v: number) => void;
  error?: string;
  t: any;
  isMandatory: boolean;
}) {
  return (
    <div id={id} className={styles.countSelectorWrapper}>
      <div
        className={`${styles.countSelector} ${error ? styles.inputError : ""}`}
      >
        <span className={styles.countLabel}>
          {label} {isMandatory && <span className={styles.asterisk}>*</span>}
        </span>
        <div className={styles.countControls}>
          <button
            type="button"
            className={styles.countBtn}
            onClick={(e) => {
              e.preventDefault();
              if (value > 0) onChange(value - 1);
            }}
          >
            -
          </button>
          <span className={styles.countValue}>{value || 0}</span>
          <button
            type="button"
            className={styles.countBtn}
            onClick={(e) => {
              e.preventDefault();
              onChange((value || 0) + 1);
            }}
          >
            +
          </button>
        </div>
      </div>
      {error && (
        <span className={styles.inlineErrorDown}>
          <MdErrorOutline /> {t(`errors.${error}`)}
        </span>
      )}
    </div>
  );
}

export function PropertySpecsSkeleton() {
  // Skeleton code remains unchanged...
  return (
    <section className={styles.transparentContainer}>
      {/* Title */}
      <div className={`${styles.skeletonPulse} ${styles.skelTitle}`} />

      {/* 1. QUANTITY SELECTORS (Rooms, Beds, Baths) */}
      <div className={styles.selectorsGrid}>
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className={styles.countSelector}>
            <div className={`${styles.skeletonPulse} ${styles.skelLabel}`} />
            <div className={styles.countControls}>
              <div
                className={`${styles.skeletonPulse} ${styles.skelCountBtn}`}
              />
              <div
                className={`${styles.skeletonPulse} ${styles.skelCountValue}`}
              />
              <div
                className={`${styles.skeletonPulse} ${styles.skelCountBtn}`}
              />
            </div>
          </div>
        ))}
      </div>

      {/* 2. AREA & DIMENSIONS ROW */}
      <div className={styles.inputGrid}>
        {[1, 2, 3].map((i) => (
          <div key={i} className={styles.inputGroup}>
            <div className={`${styles.skeletonPulse} ${styles.skelLabel}`} />
            <div className={`${styles.skeletonPulse} ${styles.skelInput}`} />
          </div>
        ))}
      </div>

      {/* 3. LEVELS, YEAR BUILT, BUYERS FEE */}
      <div className={styles.inputGrid}>
        {/* Dynamic Levels Layout Simulation */}
        <div className={styles.inputGroup}>
          <div className={`${styles.skeletonPulse} ${styles.skelLabel}`} />
          <div className={styles.levelsWrapper}>
            <div
              className={`${styles.skeletonPulse} ${styles.skelInput}`}
              style={{ flex: 1 }}
            />
            <span className={styles.levelDivider}>/</span>
            <div
              className={`${styles.skeletonPulse} ${styles.skelInput}`}
              style={{ flex: 1 }}
            />
          </div>
        </div>

        {/* Year Built & Buyers Fee */}
        {[1, 2].map((i) => (
          <div key={i} className={styles.inputGroup}>
            <div className={`${styles.skeletonPulse} ${styles.skelLabel}`} />
            <div className={`${styles.skeletonPulse} ${styles.skelInput}`} />
          </div>
        ))}
      </div>

      {/* 4. CONSTRUCTION MATERIALS */}
      <div className={styles.inputGroup}>
        <div className={`${styles.skeletonPulse} ${styles.skelLabel}`} />
        <div className={`${styles.skeletonPulse} ${styles.skelInput}`} />
      </div>
    </section>
  );
}
